/**
 * SubscriptionRequired Page
 * 
 * Standalone subscription page shown when user's subscription is expired/cancelled.
 * For promotional/free plan (store === 'promotional'), shows only "Contact support" text.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useToast } from '../contexts/ToastContext';
import { familyAPI, userAPI, revenueCatAPI, stripeAPI } from '../services/api';
import { Check, CheckCircle, AlertCircle, Clock, LogOut, Mail, ExternalLink, Smartphone, Globe, X, ArrowLeft, Plus } from 'react-feather';
import { useResponsive } from '../hooks/useResponsive';
import { useConfirm } from '../hooks/useConfirm';
import { TextInput } from '../components/ui';
import { 
  configureRevenueCat, 
  getOfferings, 
  purchasePackage,
  preparePurchase,
  Package 
} from '../services/revenueCatService';
import { PRODUCT_FEATURES, ALACARTE_OPTIONS, ALACARTE_CONTACT_EMAIL } from '../config/revenuecatConfig';
import analytics from '../services/analytics';

// KGH-790: fallback copy when a TEAM_COVERED answer carries no message.
const TEAM_COVERED_FALLBACK_MESSAGE = "Your APS Membership is included with your team, so there's nothing to purchase.";

// KGH-790: POST /stripe/create-checkout-session answers a family a live team
// grant covers with 409 { error: 'TEAM_COVERED', message } (a `code` field is
// being added). apiCall throws that with .status, the body's `code` in .code,
// and `body.error || body` in .details (today the bare 'TEAM_COVERED' string);
// .message is the human text.
const isTeamCoveredCheckoutError = (err: any): boolean =>
  err?.status === 409
  && [err.code, err.details, err.details?.code, err.details?.error].includes('TEAM_COVERED');

const SubscriptionRequired: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, sessionToken } = useAuth();
  const { family, plans, loadPlans, loadFamily } = useFamily();
  // Shared subscription poller/context — used to finalize a just-completed
  // checkout while the Stripe webhook catches up (eventual consistency).
  const { isPaid, refetch: refetchSubscription } = useSubscription();
  const toast = useToast();

  // True after we return from Stripe checkout, while we wait for the webhook
  // to flip the family to paid. Drives the "Finalizing…" screen (#179).
  const [finalizingCheckout, setFinalizingCheckout] = useState(false);
  const errorRef = useRef<HTMLDivElement | null>(null);
  const teamNoticeRef = useRef<HTMLDivElement | null>(null);
  // Handle the ?checkout_success redirect exactly once (replaceState doesn't
  // clear router searchParams, so the effect below would otherwise re-fire).
  const checkoutHandledRef = useRef(false);
  // Stable handle to the identity-unstable context refetch so the poll effect
  // isn't torn down/restarted on every context re-render.
  const refetchRef = useRef(refetchSubscription);
  useEffect(() => {
    refetchRef.current = refetchSubscription;
  }, [refetchSubscription]);

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  // One plan (Premium retired 2026-09-11): the only tier is the APS Membership.
  const [selectedTier, setSelectedTier] = useState<'regular' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // KGH-790: prepare-purchase or checkout answered TEAM_COVERED (the family's
  // team already includes the membership) — an informational notice, not the red error.
  const [teamCoveredNotice, setTeamCoveredNotice] = useState<{
    message: string;
    managementUrl: string | null;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBillingOwner, setIsBillingOwner] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [revenueCatConfigured, setRevenueCatConfigured] = useState(false);
  const [revenueCatPackages, setRevenueCatPackages] = useState<Package[]>([]);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    stripe_coupon_id: string;
    discount_type: 'percent_off' | 'amount_off';
    discount_value: number;
  } | null>(null);

  const isFreePlan = subscriptionData?.store === 'promotional';

  // Analytics: paywall viewed — feeds the paywall→subscription funnel. Fires
  // once per page mount; don't include it in any re-render loop.
  useEffect(() => {
    try {
      analytics.track('paywall_viewed', { location: 'subscription_required_page' });
    } catch (e) { /* never break render */ }
  }, []);

  // Check for cancelled/successful checkout
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      setError('Checkout was cancelled. You can try again when ready.');
      window.history.replaceState({}, '', '/subscription-required');
    }
    if (searchParams.get('checkout_success') === 'true' && !checkoutHandledRef.current) {
      // #179: don't just re-check once and drop the user back on the paywall.
      // Enter a "Finalizing…" state and poll the shared subscription context
      // until the Stripe webhook flips the family to paid. Guarded to run once.
      checkoutHandledRef.current = true;
      setFinalizingCheckout(true);
      loadFamily?.();
      void refetchRef.current();
      window.history.replaceState({}, '', '/subscription-required');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Pre-fill the promo field from ?code=… (handed off by RedeemContext for a
  // discount code) so the user doesn't have to retype it. Runs once on mount.
  useEffect(() => {
    const seeded = searchParams.get('code');
    if (seeded) setPromoCode(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #179: as soon as the subscription flips to paid, leave for the dashboard.
  useEffect(() => {
    if (finalizingCheckout && isPaid) {
      navigate('/dashboard', { replace: true });
    }
  }, [finalizingCheckout, isPaid, navigate]);

  // #179: while finalizing, poll the subscription status (webhook usually lands
  // within a few seconds) until it flips to paid or we hit a timeout, then bail
  // back to the plan picker so the user is never stuck on a spinner.
  useEffect(() => {
    if (!finalizingCheckout) return;

    const interval = setInterval(() => {
      void refetchRef.current();
    }, 2000);

    const timeout = setTimeout(() => {
      setFinalizingCheckout(false);
      setError(
        "We couldn't confirm your subscription yet. If you were charged, it may take a moment — refresh in a bit, or contact support if it doesn't update.",
      );
    }, 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [finalizingCheckout]);

  // Bring checkout/validation errors into view. The error banner sits at the top
  // of a tall page while the Subscribe button is at the bottom (#202), so scroll
  // to it whenever it changes rather than leaving it off-screen.
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);
  // Same for the TEAM_COVERED notice, which also renders at the top.
  useEffect(() => {
    if (teamCoveredNotice && teamNoticeRef.current) {
      teamNoticeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [teamCoveredNotice]);

  useEffect(() => {
    const loadData = async () => {
      if (!sessionToken) return;

      try {
        setIsLoading(true);
        
        await loadPlans();

        const profile = await userAPI.getProfile(sessionToken);
        const familyData = await familyAPI.getMyFamily(sessionToken);
        const familyMember = familyData.family?.members?.find((m: any) => m.id === profile.id);
        setIsBillingOwner(familyMember?.is_billing_owner ?? false);

        if (familyData.family?.id) {
          try {
            await configureRevenueCat(familyData.family.id);
            setRevenueCatConfigured(true);
            
            const offerings = await getOfferings();
            if (offerings.current?.availablePackages) {
              setRevenueCatPackages(offerings.current.availablePackages);
            }
            
            const status = await revenueCatAPI.getSubscriptionStatus(sessionToken);
            if (status) {
              // Derive isCanceled: active but won't renew and has expiration date
              const isCanceled = status.isActive && !status.willRenew && !!status.expirationDate;
              setSubscriptionData({
                status: status.isActive ? 'active' : 'inactive',
                isActive: status.isActive,
                tier: status.tier,
                store: status.store,
                isPromotional: status.store === 'promotional',
                expiresAt: status.expirationDate,
                willRenew: status.willRenew,
                isCanceled: isCanceled,
                managementUrl: status.managementUrl,
              });

              // If subscription is active, redirect back to app
              if (status.isActive) {
                navigate('/dashboard', { replace: true });
                return;
              }
            }
          } catch (rcErr) {
            console.warn('RevenueCat error:', rcErr);
          }
        }
      } catch (err: any) {
        console.error('Error loading subscription data:', err);
        setError(err.message || 'Failed to load subscription plans');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [sessionToken, navigate]);

  // One plan (Premium retired 2026-09-11): preselect the APS Membership for the
  // billing owner so Subscribe isn't a dead grey button until the only card is
  // clicked. Bypasses handlePlanSelect, which clears the promo field (a ?code=
  // hand-off has to survive).
  useEffect(() => {
    if (!isBillingOwner || isFreePlan || selectedPlan) return;
    const onlyPriceId = plans?.length === 1 ? plans[0].prices?.[0]?.id : undefined;
    if (onlyPriceId) {
      setSelectedPlan(onlyPriceId);
      setSelectedTier('regular');
    }
  }, [isBillingOwner, isFreePlan, plans, selectedPlan]);

  const formatPrice = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const handlePlanSelect = (planId: string, tier: 'regular') => {
    // Re-clicking the plan that's already selected (now preselected) isn't a
    // switch, so it mustn't wipe an applied discount code below.
    if (!isBillingOwner || planId === selectedPlan) return;
    setSelectedPlan(planId);
    setSelectedTier(tier);
    // Clear promo code when switching plans
    setPromoCode('');
    setPromoError(null);
    setAppliedCoupon(null);
  };

  // Handle applying promo code
  const handleApplyPromoCode = async () => {
    if (!promoCode.trim() || !sessionToken) return;

    setPromoLoading(true);
    setPromoError(null);

    try {
      // audit #213: promo codes always grant the 'regular' tier (owner decision),
      // regardless of any plan the user has selected.
      const promoTier = 'regular';
      // First validate the promo code to check what type it is
      const validateResult = await revenueCatAPI.validatePromoCode(sessionToken, promoCode.trim(), promoTier);

      if (validateResult.valid) {
        if (validateResult.type === 'discount' && validateResult.stripe_coupon_id && validateResult.discount_type && validateResult.discount_value !== undefined) {
          // This is a discount code - store it for checkout
          setAppliedCoupon({
            code: promoCode.trim().toUpperCase(),
            stripe_coupon_id: validateResult.stripe_coupon_id,
            discount_type: validateResult.discount_type as 'percent_off' | 'amount_off',
            discount_value: validateResult.discount_value,
          });
          setPromoCode('');
          setPromoError(null);
        } else {
          // This is a free access code - redeem immediately
          const redeemResult = await revenueCatAPI.redeemPromoCode(sessionToken, promoCode.trim(), promoTier);

          if (redeemResult.success) {
            setPromoCode('');
            setPromoLoading(false);
            // Confirm the unlock and refetch the subscription so the dashboard
            // reflects paid state immediately, instead of relying solely on the
            // subscription:updated socket to catch up.
            toast.success('Access unlocked!');
            void refetchRef.current();
            navigate('/dashboard', { replace: true });
            return;
          } else {
            setPromoError(redeemResult.message || redeemResult.error || 'Invalid or expired promo code');
          }
        }
      } else {
        setPromoError(validateResult.message || validateResult.error || 'Invalid or expired promo code');
      }
    } catch (err: any) {
      console.error('Error applying promo code:', err);
      setPromoError(err.message || 'Failed to apply promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  // KGH-790: explain TEAM_COVERED in place (the blue notice, not the red
  // error) and refresh the shared subscription state, since the backend may
  // have just healed the family's team access.
  const showTeamCoveredNotice = (message: string, managementUrl: string | null) => {
    setTeamCoveredNotice({ message, managementUrl });
    void refetchRef.current();
  };

  const handleSubscribe = async () => {
    if (!selectedPlan || !selectedTier || !isBillingOwner || !sessionToken) return;

    setIsSubmitting(true);
    setError('');
    setTeamCoveredNotice(null);

    try {
      // Prepare for purchase (revokes any promotional entitlements and checks cross-store)
      const prepareResult = await preparePurchase(selectedTier);

      if (!prepareResult.success) {
        // KGH-790: team-covered family — nothing to buy (409 TEAM_COVERED).
        if (prepareResult.code === 'TEAM_COVERED' || prepareResult.error === 'TEAM_COVERED') {
          const baseTeamMessage = prepareResult.message || TEAM_COVERED_FALLBACK_MESSAGE;
          // The live-store variant also says where to cancel (instructions).
          const teamMessage = prepareResult.instructions
            ? `${baseTeamMessage}\n\n${prepareResult.instructions}`
            : baseTeamMessage;
          // Hand off ONLY in the plain covered case: the backend just synced
          // the team grant to RevenueCat (access is live) and no store is
          // involved. Same hand-off as a free promo code below. Any store
          // detail means the family also pays a store and needs to read how
          // to stop; a web subscriber's managementUrl can be null, so the
          // other store fields are checked too.
          const plainCovered = prepareResult.rcSynced
            && !prepareResult.managementUrl
            && !prepareResult.instructions
            && !prepareResult.currentStore;
          if (plainCovered) {
            toast.info(teamMessage, 8000);
            void refetchRef.current();
            navigate('/dashboard', { replace: true });
            return;
          }
          // Otherwise explain in place (e.g. rcSynced:false "finishing setting
          // it up", or a store to cancel). The Manage link shows only when
          // the backend sent a managementUrl.
          showTeamCoveredNotice(teamMessage, prepareResult.managementUrl || null);
          return;
        }
        const errorMsg = prepareResult.instructions
          ? `${prepareResult.message}\n\n${prepareResult.instructions}`
          : prepareResult.message || 'Unable to process subscription';
        setError(errorMsg);
        return;
      }

      // Check if there's a promo code to apply
      // A ?code= discount is pre-filled into the field; with the plan now
      // preselected the user can press Subscribe without clicking Apply, so
      // fall back to the typed code (the checkout route validates it).
      const codeToApply = appliedCoupon?.code || promoCode.trim() || undefined;

      // P2.3: pull the paywall trigger label that brought the user here
      // (stashed by UpgradeModal). Ride it into stripe_session.metadata so
      // the Stripe webhook can fire paywall_converted attributed to the
      // gate. Pull-and-clear so a fresh checkout doesn't reuse stale data.
      let triggerAction: string | undefined;
      try {
        triggerAction = sessionStorage.getItem('paywall_trigger_action') || undefined;
        if (triggerAction) sessionStorage.removeItem('paywall_trigger_action');
      } catch {}

      // Use Stripe Checkout with returnUrl pointing back to this page
      const checkoutResult = await stripeAPI.createCheckoutSession(
        sessionToken,
        selectedTier,
        codeToApply,
        '/subscription-required', // Return here on cancel/success
        triggerAction
      );

      if (!checkoutResult.success || !checkoutResult.url) {
        setError(checkoutResult.error || 'Failed to create checkout session');
        return;
      }

      // Redirect to Stripe Checkout
      window.location.href = checkoutResult.url;
    } catch (err: any) {
      // KGH-790: checkout itself refused, because a live team grant covers the
      // family (e.g. a team seat landed after prepare-purchase answered).
      // That's the same news as prepare-purchase's TEAM_COVERED, so it gets
      // the same notice and refetch, not the red error.
      if (isTeamCoveredCheckoutError(err)) {
        showTeamCoveredNotice(
          err.message && err.message !== 'TEAM_COVERED' ? err.message : TEAM_COVERED_FALLBACK_MESSAGE,
          null,
        );
        return;
      }
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to start subscription. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // The question is unchanged; only the dialog is. window.confirm blocks the
  // main thread, cannot be styled or made keyboard-consistent, and is the one
  // native dialog left on the family surfaces.
  const { ConfirmComponent: SignOutConfirm, confirm: confirmSignOut } = useConfirm({
    title: 'Sign out',
    message: 'Are you sure you want to sign out?',
    confirmText: 'Sign out',
    cancelText: 'Cancel',
    variant: 'warning',
  });

  const handleLogout = async () => {
    if (await confirmSignOut()) {
      await logout();
      navigate('/login', { replace: true });
    }
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@apsrecruits.com?subject=Subscription%20Support';
  };

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafbff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#F9550D',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6B7280' }}>Loading subscription options...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // #179: after a successful Stripe checkout, show a finalizing state and wait
  // for the webhook to flip the family to paid rather than dumping the user
  // back onto the paywall.
  if (finalizingCheckout && !isPaid) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafbff',
        padding: '20px',
      }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#F9550D',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#151133', margin: '0 0 8px' }}>
            Finalizing your subscription…
          </h1>
          <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
            Payment received. We're activating your access — this only takes a moment.
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#fafbff',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        background: '#F9550D',
        padding: isMobile ? '24px 12px' : '40px 20px',
        borderRadius: '0 0 24px 24px',
        textAlign: 'center',
        position: 'relative',
      }}>
        {/* P2.7 — escape hatches. The page is reached via the upgrade modal
            and Stripe-cancel redirect; in freemium it should never feel
            like a dead end. Back link top-left + X top-right both go to
            /dashboard so the user keeps their free-tier access. */}
        <button
          onClick={() => navigate('/dashboard')}
          aria-label="Back to dashboard"
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            background: 'rgba(255,255,255,0.15)',
            border: 'none',
            color: 'white',
            fontSize: 13,
            fontWeight: 600,
            padding: '6px 12px',
            borderRadius: 8,
            cursor: 'pointer',
            fontFamily: 'Manrope, sans-serif',
          }}
        >
          <ArrowLeft size={14} aria-hidden style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />Back to dashboard
        </button>
        <button
          onClick={() => navigate('/dashboard')}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'rgba(255,255,255,0.15)',
            border: 'none',
            color: 'white',
            width: 32,
            height: 32,
            borderRadius: 16,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}
        >
          <X size={18} aria-hidden />
        </button>

        <AlertCircle size={48} color="white" style={{ marginBottom: '16px' }} />
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: 'white',
          margin: '0 0 8px 0',
        }}>
          {searchParams.get('cancelled') === 'true' ? 'Checkout cancelled' : 'Become an APS Member'}
        </h1>
        <p style={{
          fontSize: '16px',
          color: 'rgba(255, 255, 255, 0.8)',
          margin: 0,
        }}>
          {searchParams.get('cancelled') === 'true'
            ? "You're still on the free plan — no charge. Try again whenever you're ready."
            : subscriptionData?.isCanceled
              ? 'Your subscription has been cancelled. Renew your APS Membership to keep your paid features.'
              : 'Unlock coach contacts, outbound messaging, and analytics.'}
        </p>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        padding: isMobile ? '16px 12px' : '24px',
        maxWidth: isMobile ? '100%' : '600px',
        margin: '0 auto',
        width: '100%',
      }}>
        {/* Expiration Banner */}
        {subscriptionData?.expiresAt && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            background: '#FEE2E2',
            borderRadius: '12px',
            marginBottom: '20px',
          }}>
            <Clock size={20} color="#DC2626" />
            <span style={{ fontSize: '14px', color: '#DC2626' }}>
              {new Date(subscriptionData.expiresAt) < new Date()
                ? `Subscription expired on ${new Date(subscriptionData.expiresAt).toLocaleDateString()}`
                : `Subscription expires on ${new Date(subscriptionData.expiresAt).toLocaleDateString()}`
              }
            </span>
          </div>
        )}

        {/* Non-billing owner message (#235). Shown for any non-owner (and when
            the family fetch failed, which leaves isBillingOwner false) so they
            get a reason instead of an empty plan list. */}
        {!isBillingOwner && !isFreePlan && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '16px',
            background: '#DBEAFE',
            borderRadius: '12px',
            marginBottom: '20px',
          }}>
            <AlertCircle size={20} color="#2563EB" style={{ flexShrink: 0, marginTop: '2px' }} />
            <span style={{ fontSize: '14px', color: '#2563EB' }}>
              Only the billing owner can manage subscriptions. Please contact the family billing owner to renew.
            </span>
          </div>
        )}

        {/* Free plan support message */}
        {isFreePlan && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '16px',
            background: '#DBEAFE',
            borderRadius: '12px',
            marginBottom: '20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <AlertCircle size={20} color="#2563EB" style={{ flexShrink: 0, marginTop: '2px' }} />
              <span style={{ fontSize: '14px', color: '#2563EB' }}>
                This account has a promotional subscription. For assistance, please contact support.
              </span>
            </div>
            <button
              onClick={handleContactSupport}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: '#BFDBFE',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#2563EB',
                cursor: 'pointer',
                alignSelf: 'flex-start',
              }}
            >
              <Mail size={16} />
              Contact Support
            </button>
          </div>
        )}

        {error && (
          <div ref={errorRef} style={{
            padding: '12px 16px',
            marginBottom: '20px',
            background: '#FEE2E2',
            border: '1px solid #FECACA',
            borderRadius: '12px',
            color: '#DC2626',
            fontSize: '14px',
            whiteSpace: 'pre-line',
          }}>
            {error}
          </div>
        )}

        {/* KGH-790: TEAM_COVERED from prepare-purchase or checkout — the team
            already includes the membership. Informational (not the red
            error); the Manage link renders only when the backend sent a
            managementUrl (a live store the family can cancel). */}
        {teamCoveredNotice && (
          <div ref={teamNoticeRef} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '16px',
            background: '#DBEAFE',
            borderRadius: '12px',
            marginBottom: '20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <CheckCircle size={20} color="#2563EB" style={{ flexShrink: 0, marginTop: '2px' }} />
              <span style={{ fontSize: '14px', color: '#2563EB', whiteSpace: 'pre-line' }}>
                {teamCoveredNotice.message}
              </span>
            </div>
            {teamCoveredNotice.managementUrl && (
              <a
                href={teamCoveredNotice.managementUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: '#BFDBFE',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#2563EB',
                  textDecoration: 'none',
                  alignSelf: 'flex-start',
                }}
              >
                Manage subscription
                <ExternalLink size={16} />
              </a>
            )}
          </div>
        )}

        {/* Plan Selection - only for billing owner and non-free plan */}
        {isBillingOwner && !isFreePlan && plans && plans.length > 0 && (
          <>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#151133',
              marginBottom: '16px',
            }}>
              Select your membership
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              {plans.map((plan: any) => {
                const price = plan.prices?.[0];
                if (!price) return null;

                // One plan (Premium retired 2026-09-11): the APS Membership is the
                // 'regular' tier, so its included-features list is the only one.
                const isSelected = selectedPlan === price.id;
                const features = PRODUCT_FEATURES.regular;

                return (
                  <div
                    key={price.id}
                    onClick={() => handlePlanSelect(price.id, 'regular')}
                    style={{
                      position: 'relative',
                      padding: '20px',
                      background: isSelected ? 'rgba(249, 85, 13, 0.1)' : 'white',
                      border: `2px solid ${isSelected ? '#F9550D' : '#D1D5DB'}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#151133', margin: '0 0 8px 0' }}>
                        {plan.name}
                      </h3>
                      {price && (
                        <>
                          <div style={{ fontSize: '24px', fontWeight: '700', color: '#F9550D' }}>
                            {formatPrice(price.amount, price.currency)}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6B7280' }}>
                            per {price.interval}
                          </div>
                        </>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {features.map((feature, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Check size={16} color="#F9550D" />
                          <span style={{ fontSize: '14px', color: '#151133' }}>{feature}</span>
                        </div>
                      ))}
                    </div>

                    {isSelected && (
                      <div style={{ position: 'absolute', top: '16px', left: '16px' }}>
                        <CheckCircle size={24} color="#F9550D" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* À-la-carte add-ons (KGH-785). Listed, NOT purchasable in-app —
                the client wants these framed as "contact for pricing" so users
                email us rather than expecting an in-cart add-on (also keeps us
                clear of App Store IAP rules for off-platform services). Neutral
                info panel — orange stays reserved for the buy CTA. */}
            <div style={{
              marginBottom: '24px',
              padding: '20px',
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#151133', margin: '0 0 6px 0' }}>
                Additional features available
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 14px 0', lineHeight: 1.5 }}>
                Interested in more hands-on support? These services are offered separately —
                reach out and we'll share pricing.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {ALACARTE_OPTIONS.map((option, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Plus size={16} color="#6B7280" />
                    <span style={{ fontSize: '14px', color: '#151133' }}>{option}</span>
                  </div>
                ))}
              </div>
              <a
                href={`mailto:${ALACARTE_CONTACT_EMAIL}?subject=${encodeURIComponent('APS additional services inquiry')}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: '#BFDBFE',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#2563EB',
                  textDecoration: 'none',
                }}
              >
                <Mail size={16} />
                Contact us for pricing
              </a>
            </div>

            {/* Promo or Team Code Section. Team codes are partner-issued
                bulk-redemption codes — same redemption surface, distinct
                concept in the user's mental model. Always surface both. */}
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#151133', marginBottom: '8px' }}>
                Have a Promo or Team Code?
              </h3>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                {/* flex:1 keeps it beside Apply; the primitive is width:100% by
                    default and would otherwise push the button off the row. The
                    hand-rolled focus/invalid borders are gone with the native
                    input — .aps-input draws both, and the outline it no longer
                    sets is the focus ring the audit found missing. */}
                <TextInput
                  type="text"
                  aria-label="Promo or team code"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toUpperCase());
                    setPromoError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && promoCode.trim() && !promoLoading) {
                      handleApplyPromoCode();
                    }
                  }}
                  placeholder="Enter promo or team code"
                  disabled={promoLoading}
                  invalid={!!promoError}
                  style={{ flex: 1 }}
                />
                <button
                  onClick={handleApplyPromoCode}
                  disabled={promoLoading || !promoCode.trim()}
                  style={{
                    padding: '12px 24px',
                    background: (promoLoading || !promoCode.trim()) ? '#D1D5DB' : '#F9550D',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: (promoLoading || !promoCode.trim()) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '80px',
                  }}
                  onMouseEnter={(e) => {
                    if (!promoLoading && promoCode.trim()) e.currentTarget.style.background = '#e04a0a';
                  }}
                  onMouseLeave={(e) => {
                    if (!promoLoading && promoCode.trim()) e.currentTarget.style.background = '#F9550D';
                  }}
                >
                  {promoLoading ? (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }} />
                  ) : 'Apply'}
                </button>
              </div>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: '0' }}>
                Enter a promo or team code to get free access or a discount
              </p>
              {promoError && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '8px',
                  padding: '8px 12px',
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  borderRadius: '6px',
                }}>
                  <AlertCircle size={14} color="#EF4444" />
                  <span style={{ fontSize: '13px', color: '#EF4444' }}>{promoError}</span>
                </div>
              )}
              {appliedCoupon && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '12px',
                  padding: '12px 16px',
                  background: '#ECFDF5',
                  border: '1px solid #6EE7B7',
                  borderRadius: '8px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={16} color="#059669" />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                      {appliedCoupon.code}
                    </span>
                    <span style={{ fontSize: '13px', color: '#059669' }}>
                      {appliedCoupon.discount_type === 'percent_off'
                        ? `${appliedCoupon.discount_value}% off`
                        : `$${(appliedCoupon.discount_value / 100).toFixed(2)} off`}
                    </span>
                  </div>
                  <button
                    onClick={() => setAppliedCoupon(null)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <X size={16} color="#059669" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* #235: billing owner but no plans came back (plan/family fetch issue) —
            explain instead of showing an empty picker with a dead Subscribe button. */}
        {isBillingOwner && !isFreePlan && (!plans || plans.length === 0) && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '16px',
            background: 'var(--aps-fill)',
            border: '1px solid var(--aps-border-strong)',
            borderRadius: '12px',
            marginBottom: '20px',
          }}>
            <AlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px', color: 'var(--aps-text-2)' }} />
            <span style={{ fontSize: '14px', color: 'var(--aps-text-2)' }}>
              We couldn't load subscription plans right now. Please refresh in a moment, or contact support if this keeps happening.
            </span>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {isBillingOwner && !isFreePlan && (
            <button
              onClick={handleSubscribe}
              disabled={!selectedPlan || isSubmitting}
              style={{
                padding: '16px',
                background: (!selectedPlan || isSubmitting) ? '#D1D5DB' : '#F9550D',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                cursor: (!selectedPlan || isSubmitting) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {isSubmitting ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }} />
                  Processing...
                </>
              ) : (
                'Subscribe Now'
              )}
            </button>
          )}

          {/* P2.7 — "Maybe later" alongside the existing Sign Out option.
              Freemium users need an obvious back-to-free path that doesn't
              involve fully logging out. */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '14px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#151133',
              cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            Maybe later — keep me on the free plan
          </button>

          <button
            onClick={handleLogout}
            style={{
              padding: '16px',
              background: 'transparent',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              color: '#6B7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <LogOut size={18} />
            Sign out
          </button>
        </div>

        {/* Terms */}
        {!isFreePlan && (
          <p style={{
            fontSize: '12px',
            color: '#9CA3AF',
            textAlign: 'center',
            marginTop: '24px',
            lineHeight: '18px',
          }}>
            By subscribing, agreement to our Terms of Service and Privacy Policy is implied.
            Payment will be processed through Stripe.
          </p>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <SignOutConfirm />
    </div>
  );
};

export default SubscriptionRequired;
